<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  activity: {
    type: Array,
    required: true,
  },
});

const { t, locale } = useI18n();

const ACTIVITY_CONFIG = {
  created: {
    icon: 'i-lucide-sparkles',
    colorClass: 'text-blue-500 bg-blue-50 dark:bg-blue-950',
  },
  product_added: {
    icon: 'i-lucide-package-plus',
    colorClass: 'text-green-500 bg-green-50 dark:bg-green-950',
  },
  product_removed: {
    icon: 'i-lucide-package-minus',
    colorClass: 'text-red-500 bg-red-50 dark:bg-red-950',
  },
  message_prepared: {
    icon: 'i-lucide-pencil-line',
    colorClass: 'text-amber-500 bg-amber-50 dark:bg-amber-950',
  },
  message_sent: {
    icon: 'i-lucide-send',
    colorClass: 'text-teal-500 bg-teal-50 dark:bg-teal-950',
  },
  follow_up_scheduled: {
    icon: 'i-lucide-clock',
    colorClass: 'text-purple-500 bg-purple-50 dark:bg-purple-950',
  },
  converted: {
    icon: 'i-lucide-badge-check',
    colorClass: 'text-green-600 bg-green-50 dark:bg-green-950',
  },
  project_edited: {
    icon: 'i-lucide-pencil',
    colorClass: 'text-n-slate-10 bg-n-alpha-2',
  },
};

const sortedActivity = computed(() =>
  [...props.activity].sort((a, b) => new Date(b.date) - new Date(a.date))
);

const getConfig = type =>
  ACTIVITY_CONFIG[type] ?? {
    icon: 'i-lucide-circle',
    colorClass: 'text-n-slate-10 bg-n-alpha-2',
  };
</script>

<template>
  <div class="flex flex-col gap-0">
    <div class="px-4 py-3">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('PURCHASE_PROJECTS.DETAIL.ACTIVITY_TITLE') }}
      </span>
    </div>

    <div class="flex flex-col px-4 pb-3">
      <div
        v-for="(entry, idx) in sortedActivity"
        :key="idx"
        class="flex gap-3 items-start py-2"
      >
        <div class="relative flex flex-col items-center shrink-0">
          <span
            class="flex items-center justify-center size-7 rounded-full"
            :class="getConfig(entry.type).colorClass"
          >
            <span class="size-3.5" :class="getConfig(entry.type).icon" />
          </span>
          <div
            v-if="idx < sortedActivity.length - 1"
            class="w-px flex-1 min-h-[1rem] bg-n-strong mt-1"
          />
        </div>

        <div class="flex flex-col min-w-0 pb-2">
          <span class="text-sm text-n-slate-12 leading-snug">
            {{ entry.label }}
          </span>
          <span class="text-xs text-n-slate-10 mt-0.5">
            {{ dynamicTime(entry.date, locale) }}
          </span>
        </div>
      </div>

      <p
        v-if="!sortedActivity.length"
        class="py-4 text-sm text-center text-n-slate-11"
      >
        —
      </p>
    </div>
  </div>
</template>
